import { Filter, getStaticFilter } from '@/api/models';
import { AdvancedTable } from '@/components/ui/AdvancedTable';
import { Button } from '@/components/ui/Button';
import { CardBox } from '@/components/ui/CardBox';
import { mdiDelete, mdiEye, mdiPencil } from '@mdi/js';
import * as React from 'react';

export interface FiltersTableProps {
  filters: Filter[];
  isLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (key: string) => void;
  onRowClick: (filter: Filter) => void;
  onEditFilter: (filter: Filter) => void;
  onDeleteFilter: (filter: Filter) => void;
  isDeleting: boolean;
}

export const FiltersTable: React.FC<FiltersTableProps> = ({
  filters,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onEditFilter,
  onDeleteFilter,
  isDeleting,
}) => {
  const columns = [
    { key: 'emoji', label: '', sortable: false, className: 'w-12' },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'status', label: 'Status', sortable: false, className: 'w-24' },
    {
      key: 'mandatory',
      label: 'Mandatory',
      sortable: false,
      className: 'w-28',
    },
    {
      key: 'businessCount',
      label: 'Businesses',
      sortable: false,
      className: 'w-28',
    },
    { key: 'createdAt', label: 'Created', sortable: true, className: 'w-32' },
  ];

  const renderCell = (
    key: string,
    value: string | number | boolean | string[] | undefined,
    item: Filter,
  ): React.ReactNode => {
    const staticFilter = getStaticFilter(item.name);

    switch (key) {
      case 'emoji':
        return <span className='text-2xl'>{staticFilter?.emoji || '🏷️'}</span>;
      case 'name':
        return (
          <div>
            <div className='font-medium text-grey-900'>
              {staticFilter?.label || item.name}
            </div>
            <div className='text-sm text-grey-500'>{item.name}</div>
          </div>
        );
      case 'status':
        return item.enabled ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
            Enabled
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
            Disabled
          </span>
        );
      case 'mandatory':
        return item.isMandatory ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700'>
            Yes
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-700'>
            No
          </span>
        );
      case 'businessCount':
        return (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-coffice-blue/10 text-coffice-blue'>
            {item.businessCount ?? 0}
          </span>
        );
      case 'createdAt':
        return (
          <div className='text-sm text-grey-900'>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleDateString()
              : 'N/A'}
          </div>
        );
      default:
        return String(value || '-');
    }
  };

  const renderActions = (filter: Filter) => (
    <div className='flex items-center space-x-2'>
      <Button
        icon={mdiEye}
        small
        color='secondary'
        title='View Details'
        onClick={() => onRowClick(filter)}
      />
      <Button
        icon={mdiPencil}
        small
        color='secondary'
        title='Edit Filter'
        onClick={() => onEditFilter(filter)}
      />
      <Button
        icon={mdiDelete}
        small
        color='danger'
        title='Delete Filter'
        loading={isDeleting}
        onClick={() => onDeleteFilter(filter)}
      />
    </div>
  );

  return (
    <CardBox hasTable>
      <AdvancedTable
        columns={columns}
        data={filters}
        loading={isLoading}
        onSort={onSort}
        sortKey={sortBy}
        sortDirection={sortOrder}
        renderCell={renderCell}
        renderActions={renderActions}
        onRowClick={onRowClick}
        emptyMessage='No filters found. Try adjusting your search criteria.'
      />
    </CardBox>
  );
};
